import { createContext, useCallback, useMemo } from 'react';
import type { QueryKey } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';
import config from '@config/index';
import { organizationKeys, vendorKeys } from '@config/queryKeys';
import routes from '@config/routes';
import { useApp } from '@context/AppContext';
import { useContextVendorId } from '@context/VendorContext/useContextVendorId';
import { useSnackbar } from '@goodfynd/context';
import { Loading } from '@goodfynd/ui';
import { stringUtil } from '@goodfynd/utils';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import Custom500 from 'pages/500';
import { vendorUtil } from 'utils/index';
import { getOrganizationRoutePath } from 'utils/routes-util';

import { useProfileApi } from '../../hooks/api';
import { useOrganizationApi } from '../../hooks/api/useOrganizationApi';
import type { OrganizationProfile } from '../../types/organizationProfile';
import type { OrganizationMemberType } from '../../types/shared';
import type { VendorProfile } from '../../types/vendorProfile';

import type {
  OrganizationContextValue,
  OrganizationDispatchContextValue,
  OrganizationProviderProps,
} from './types';
import { useContextOrganizationId } from './useContextOrganizationId';

export const OrganizationContext = createContext<
  OrganizationContextValue | undefined
>(undefined);
export const OrganizationDispatchContext = createContext<
  OrganizationDispatchContextValue | undefined
>(undefined);

export default observer(function OrganizationProvider({
  children,
}: OrganizationProviderProps) {
  const { isLoggedIn } = useApp();
  const queryClient = useQueryClient();
  const router = useRouter();
  const snackBar = useSnackbar();

  // TODO: REMOVE after org redesign
  const vendorId = useContextVendorId();
  const profileApi = useProfileApi();

  const { data: orgVendor, isLoading: orgVendorIsLoading } =
    useQuery<VendorProfile>(
      vendorKeys.profile(vendorId),
      () => {
        return profileApi.vendorProfile(vendorId as string);
      },
      {
        enabled: isLoggedIn == true && !!vendorId && vendorId !== '~',
      }
    );
  // TODO END

  // Fetch organization info
  const api = useOrganizationApi();

  const organizationId = useContextOrganizationId(orgVendor?.organizationId);
  const queryKey: QueryKey = useMemo(
    () => organizationKeys.profile(organizationId),
    [organizationId]
  );
  const {
    data: organization = {} as OrganizationProfile,
    error,
    isLoading,
    isFetched,
    isFetching,
    refetch: refetchOrganization,
  } = useQuery<OrganizationProfile, unknown>(
    queryKey,
    () => {
      return profileApi.organizationProfile(organizationId as string);
    },
    {
      enabled: isLoggedIn === true && !!organizationId,
    }
  );

  const invalidateOrganization = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const setParentOrganization = useCallback(
    async (id: string, type: OrganizationMemberType) => {
      let success = false;

      try {
        await api.setParent(id, organization.nameId, type);
        queryClient.refetchQueries([
          config.queryKeys.organizationStructure,
          organization.id,
        ]);
        router.push(
          getOrganizationRoutePath(
            routes.organization.info.structure,
            organization.nameId
          )
        );
        success = true;
      } catch (error: any) {
        console.error(error);
        snackBar.openSnackbar({
          message: error?.message || 'Unknown error occurred.',
          type: 'error',
        });
      }

      return success;
    },
    [api, organization.id, organization.nameId, queryClient, router, snackBar]
  );

  const value = useMemo(
    () => ({
      hasError: isFetched ? !!error : undefined,
      isLoading: isLoading || orgVendorIsLoading,
      isFetched,
      isFetching,
      isOrganization: !!router?.query?.organizationId,
      organization,
      organizationId: organization.id,
      organizationNameId: organization.nameId,
    }),
    [
      error,
      isFetched,
      isFetching,
      isLoading,
      orgVendorIsLoading,
      organization,
      router?.query?.organizationId,
    ]
  );

  const renderContent = useCallback(() => {
    if (isLoading) {
      return <Loading type="line" />;
    }

    if (error) {
      return <Custom500 />;
    }

    return children;
  }, [children, error, isLoading]);

  return (
    <OrganizationContext.Provider value={value}>
      <OrganizationDispatchContext.Provider
        value={{
          changeOrganization: useCallback(
            (orgId: string) => {
              router.push(
                stringUtil.replace(routes.organization.overview, {
                  ':organizationId': orgId,
                })
              );
            },
            [router]
          ),
          invalidateOrganization,
          isCurrentOrganization: (entity) =>
            vendorUtil.idEquals(organizationId, entity),
          refetchOrganization,
          setParentOrganization,
        }}
      >
        {renderContent()}
      </OrganizationDispatchContext.Provider>
    </OrganizationContext.Provider>
  );
});
